import { AxiosInstance, AxiosRequestConfig, HttpStatusCode } from 'axios';

import { storageKeys } from '@/app/constants/storageKeys';
import { isTokenExpired } from '@/app/utils/auth/isTokenExpired';
import { decode } from '@/app/utils/auth/jwt';
import { redirectToAuth } from '@/app/utils/auth/redirect';
import { ApiError } from '@/infra/exceptions/instances/ApiError';
import { storage } from '@/infra/protocols/storage';
import { authService } from '@/modules/auth/services';

type RequestRetryCallBack = (token: string) => void;

let isRefreshing = false;
let subscribers: RequestRetryCallBack[] = [];

function subscribeTokenRefresh(cb: RequestRetryCallBack) {
  subscribers.push(cb);
}

function onRefreshed(token: string) {
  subscribers.forEach((cb) => cb(token));
  subscribers = [];
}

const logoutUser = () => location.assign('/logout');

function tryToRefreshToken(client: AxiosInstance, requestToRetry?: AxiosRequestConfig) {
  const refreshToken = storage.get(storageKeys.REFRESH_TOKEN);
  const decodedRefreshToken = decode(refreshToken);

  if (isTokenExpired(decodedRefreshToken?.exp)) {
    redirectToAuth();
    return Promise.reject(
      new ApiError({
        errors: [],
        traceId: '',
        title: 'Necessária a reautenticação.',
        status: HttpStatusCode.Unauthorized.toString(),
      }),
    );
  }

  if (!isRefreshing) {
    isRefreshing = true;

    authService
      .refreshToken(refreshToken)
      .then((response) => {
        storage.add(storageKeys.ACCESS_TOKEN, response.accessToken);
        storage.add(storageKeys.REFRESH_TOKEN, response.refreshToken);

        onRefreshed(response.accessToken);
        isRefreshing = false;
      })
      .catch(logoutUser);
  }

  const requestSubscribers = new Promise((resolve) => {
    subscribeTokenRefresh((token) => {
      if (requestToRetry) {
        requestToRetry.headers = {
          ...requestToRetry.headers,
          Authorization: `Bearer ${token}`,
        };
        resolve(client(requestToRetry));
      }
    });
  });

  return requestSubscribers;
}

export { tryToRefreshToken };
